import React, { useRef, useState } from "react";
import { FileUploadService } from "../../api";
import { Body_upload_file_docrepo_upload_post } from "../../api/models/Body_upload_file_docrepo_upload_post";
import { shortenFileName } from "./utils/fileNameHelpers";
import DocumentViewer from "../service_request_manage/components/DocumentViewer";

interface Props {
  collection_type: string;
  collection_ref: string;
  repo_type: "RESUME" | "JD" | "REPORTS";
  uploadMode: "draft" | "final";
  role: "Admin" | "User";
  label: string;
  fileUrl?: string | null;
  onChange: (url: string | null) => void;

  enableURL?: boolean;
  maxFileSizeKB?: number;
  allowedFileTypes?: string[];
}

const DEFAULT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

const SingleFileUploadRow: React.FC<Props> = ({
  collection_type,
  collection_ref,
  repo_type,
  uploadMode,
  role,
  label,
  fileUrl,
  onChange,
  enableURL = true,
  maxFileSizeKB = 150,
  allowedFileTypes = DEFAULT_TYPES,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fileName = selectedFile?.name || fileUrl?.split("/").pop();
  const displayName = fileName ? shortenFileName(fileName) : null;

  const openFileDialog = () => fileInputRef.current?.click();

  const handleFileChange = (file: File) => {
    setError(null);

    if (file.size / 1024 > maxFileSizeKB) {
      setError(`Max ${maxFileSizeKB} KB allowed`);
      return;
    }

    if (!allowedFileTypes.includes(file.type)) {
      setError("Invalid file type");
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);

      const body: Body_upload_file_docrepo_upload_post = {
        files: [selectedFile],
      };

      const res = await FileUploadService.uploadFileDocrepoUploadPost(
        collection_type,
        collection_ref,
        repo_type,
        body,
        uploadMode
      );

      const uploadedUrl = res.uploaded?.[0]?.url;
      if (!uploadedUrl) return;

      onChange(uploadedUrl);   // 🔥 parent handles refresh
      setSelectedFile(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <div className="border rounded px-3 py-2 flex flex-wrap items-center gap-3">

        {/* LEFT: Label + File Name */}
        <div className="flex items-center gap-3 flex-1 min-w-0">

          <h4 className="font-medium text-sm whitespace-nowrap">
            {label}
          </h4>

          <div className="text-sm truncate">
            {displayName ? (
              enableURL && fileUrl && !selectedFile ? (
                <button
                  onClick={() => setViewerOpen(true)}
                  className="text-blue-600 hover:underline truncate"
                  title={fileName}
                >
                  📄 {displayName}
                </button>
              ) : (
                <span className="text-gray-700 truncate" title={fileName}>
                  📄 {displayName}
                </span>
              )
            ) : role === "Admin" ? (
              <span className="text-gray-700">Upload Pending</span>
            ) : (
              <span className="text-gray-400">No file uploaded</span>
            )}
          </div>
        </div>

        {/* RIGHT: Buttons */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept={allowedFileTypes.join(",")}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileChange(file);
            }}
          />

          {role === "User" && !selectedFile && (
            <button
              onClick={openFileDialog}
              title={`Max ${maxFileSizeKB} KB • ${allowedFileTypes
                .map((t) => t.split("/")[1])
                .join(", ")
                .toUpperCase()}`}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm whitespace-nowrap"
            >
              Upload
            </button>
          )}

          {selectedFile && (
            <>
              <button
                onClick={handleUpload}
                disabled={loading}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm"
              >
                {loading ? "Saving..." : "Upload"}
              </button>

              <button
                onClick={() => setSelectedFile(null)}
                  className="bg-orange-500 text-white px-3 py-1 rounded text-sm whitespace-nowrap"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="w-full text-xs text-red-600">
            {error}
          </div>
        )}
      </div>

      {/* ✅ KEEP VIEWER OUTSIDE */}
      <DocumentViewer
        fileMeta={
          fileName && fileUrl && !selectedFile
            ? { collection_type, collection_ref, repo_type, fileName }
            : null
        }
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={displayName || "Document Viewer"}
      />
    </>
  );

};

export default SingleFileUploadRow;